import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useBasketSection from '~/hooks/use-basket-section';
import useQuoteRequirements from '~/hooks/use-quote-requirements';
import { getCountrySwitchResult } from '~/lib/basket';
import { generateBasicQuoteHref } from '~/lib/quotes';
import { QuoteAddress, QuoteParcels } from '~/lib/types/basket-types';

import { BasketSections } from '~/lib/types/enums';

export default function usePostcodeRedirect() {
  const origin = useBasketSection<QuoteAddress>({ name: BasketSections.ORIGIN });
  const destination = useBasketSection<QuoteAddress>({ name: BasketSections.DESTINATION });
  const parcels = useBasketSection<QuoteParcels>({ name: BasketSections.PARCELS });
  const quoteRequirements = useQuoteRequirements();
  const navigate = useNavigate();

  const redirects = useMemo(() => {
    const originSwitch = getCountrySwitchResult(origin.data, quoteRequirements.countries.origin);
    const destinationSwitch = getCountrySwitchResult(destination.data, quoteRequirements.countries.destination);
    return {
      origin: originSwitch,
      destination: destinationSwitch,
      suggestNewCountryQuote:
        (originSwitch.shouldRedirect && !originSwitch.serviceSupportsCountry && !!originSwitch.redirect) ||
        (destinationSwitch.shouldRedirect && !destinationSwitch.serviceSupportsCountry && !!destinationSwitch.redirect)
    };
  }, [origin, destination, quoteRequirements]);

  const onClick = useCallback(() => {
    const params = generateBasicQuoteHref(
      redirects.origin.redirect || origin.data.country,
      redirects.destination.redirect || destination.data.country,
      parcels.data.parcels[0]?.weight || 0,
      parcels.data.parcels[0]?.height || undefined,
      parcels.data.parcels[0]?.width || undefined,
      parcels.data.parcels[0]?.length || undefined
    );
    navigate(`../../order-process/quote?${params}`, { relative: 'path' });
  }, [origin, destination, parcels, redirects, navigate]);

  return { redirects, onClick };
}
